/**
 * Ungeklärte Zahlungen — Backoffice-Sicht auf `unmatched_provider_events`
 * (sql/106_unmatched_provider_events.sql, C-03/WELLE 1, Bughunt 2026-07-11: „Geld darf nie stumm
 * versickern"). Der Stripe-Webhook (routes/stripe-webhook.ts) hält hier JEDES Event dauerhaft
 * fest, das er nicht auf eine lokale Zahlungszeile anwenden konnte — z. B. weil die Zeile
 * inzwischen gelöscht/verändert wurde, der Connect-Account nicht passte, ein Statuswechsel
 * blockiert war, oder eine Teil-Rückerstattung/mögliche Überzahlung eine manuelle Nacharbeit
 * braucht. Bis zu dieser Seite (C-03-Hinweis, aufgaben.md) war die Tabelle die EINZIGE Sicht
 * darauf — kein Betreiber/Antoine konnte im Tool erkennen, dass so ein Fall aufgetreten ist.
 *
 * Quelle ist `GET /api/system/unmatched-provider-events` (owner/admin, wie `/api/system/errors`
 * und `/api/system/emails` — Stripe-Objekt-IDs und der rohe Event-Payload sind betriebsinterne
 * Daten). REIN LESEND: es existiert kein Schreibpfad, der `resolved_at` setzt (Stand 28.08.2026),
 * deshalb kein „als erledigt markieren"-Knopf in dieser ersten Version — die Spalte ist in der DB
 * angelegt, aber ungenutzt; würde sie später bespielt, ist das eine eigene Folgearbeit.
 *
 * PREISINTEGRITÄT: es wird ausschließlich angezeigt, was die Zeile enthält (inkl. `raw` als
 * Rohtext) — kein Betrag wird hier berechnet, umgerechnet oder aufaddiert.
 */
import { useApi } from "../hooks/useApi";
import { formatDateTime } from "../ui/format";
import { Card, EmptyState, ErrorNote, Icon, LoadingRows, PageHeader, StatusPill } from "../ui/components";
import type { PillTone } from "../ui/format";

interface UnmatchedProviderEventZeile {
  id: string;
  provider: string;
  providerEventId: string | null;
  eventType: string | null;
  paymentId: string | null;
  tenantId: string | null;
  reportedStatus: string | null;
  reason: string;
  raw: unknown;
  createdAt: string;
  resolvedAt: string | null;
}

/**
 * Grund-Codes, die routes/stripe-webhook.ts und apply_stripe_payment (sql/124) tatsächlich
 * schreiben (s. dortige Kommentare). Ein unbekannter Code crasht nicht — Fallback zeigt den
 * Rohcode mit neutralem Ton, analog `eventStatusMeta`/`emailStatusMeta` (ui/format.ts).
 */
const REASON_META: Record<string, { label: string; tone: PillTone }> = {
  unknown_payment: { label: "Unbekannte Zahlung", tone: "danger" },
  account_mismatch: { label: "Fremder Connect-Account", tone: "danger" },
  amount_mismatch: { label: "Betrag weicht ab", tone: "danger" },
  currency_mismatch: { label: "Währung weicht ab", tone: "danger" },
  link_mismatch: { label: "Anderer/rotierter Link", tone: "warn" },
  bad_status: { label: "Unbekannter Status", tone: "warn" },
  transition_blocked: { label: "Statuswechsel blockiert", tone: "danger" },
  possible_overpayment: { label: "Mögliche Überzahlung", tone: "danger" },
  partial_refund: { label: "Teil-Rückerstattung", tone: "warn" },
};
function reasonMeta(reason: string): { label: string; tone: PillTone } {
  return REASON_META[reason] ?? { label: reason, tone: "neutral" };
}

/** Roher `raw`-Payload als aufklappbare "Technische Details" — analog MailFehlerHinweis
 *  (ui/components.tsx), damit Support/Antoine den vollständigen Event-Ausschnitt einsehen kann. */
function RohPayload({ raw }: { raw: unknown }) {
  if (raw == null) return null;
  const text = typeof raw === "string" ? raw : JSON.stringify(raw, null, 2);
  return (
    <details className="mt-1.5">
      <summary className="cursor-pointer select-none font-label-md text-[11px] font-medium text-secondary hover:text-on-surface-variant dark:text-secondary-fixed-dim">
        Roher Payload-Ausschnitt
      </summary>
      <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md bg-surface-container-low px-2.5 py-2 font-mono text-[11px] leading-relaxed text-secondary dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
        {text}
      </pre>
    </details>
  );
}

function Zeile({ zeile }: { zeile: UnmatchedProviderEventZeile }) {
  const meta = reasonMeta(zeile.reason);
  return (
    <li className="flex flex-col gap-1.5 border-b border-[#F6F0E4] px-[18px] py-3.5 last:border-b-0 dark:border-white/[0.05]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 truncate font-body-md text-body-md font-medium text-on-background dark:text-inverse-on-surface">
          {zeile.eventType ?? "(unbekannter Event-Typ)"}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <StatusPill meta={meta} />
          {zeile.resolvedAt && <StatusPill meta={{ label: "Erledigt", tone: "success" }} />}
          <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" title={zeile.createdAt}>
            {formatDateTime(zeile.createdAt)}
          </span>
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        <span className="inline-flex min-w-0 items-center gap-1">
          <Icon name="tag" className="text-[14px]" />
          <span className="truncate font-mono">{zeile.providerEventId ?? "?"}</span>
        </span>
        {zeile.reportedStatus && (
          <>
            <span className="text-outline-variant">·</span>
            <span>gemeldeter Status: {zeile.reportedStatus}</span>
          </>
        )}
        {zeile.paymentId && (
          <>
            <span className="text-outline-variant">·</span>
            <span className="inline-flex min-w-0 items-center gap-1">
              <Icon name="receipt_long" className="text-[14px]" />
              Zahlung <span className="truncate font-mono">{zeile.paymentId}</span>
            </span>
          </>
        )}
      </div>
      <RohPayload raw={zeile.raw} />
    </li>
  );
}

export default function UnmatchedProviderEventsPage() {
  const { data, loading, error, reload } = useApi<UnmatchedProviderEventZeile[]>("/api/system/unmatched-provider-events");
  const zeilen = data ?? [];

  return (
    <>
      <PageHeader
        title="Ungeklärte Zahlungen"
        subtitle="Stripe-Webhook-Ereignisse, die sich beim Eintreffen keiner lokalen Zahlung zuordnen ließen — z. B. weil die Zahlungszeile inzwischen gelöscht wurde, ein Statuswechsel blockiert war oder eine Teil-Rückerstattung manuelle Nacharbeit braucht."
      />

      {error && <ErrorNote message={error} onRetry={reload} />}

      {loading ? (
        <Card>
          <LoadingRows rows={4} />
        </Card>
      ) : !error && zeilen.length === 0 ? (
        <Card>
          <EmptyState
            icon="task_alt"
            title="Keine ungeklärten Zahlungs-Events"
            hint="Sobald ein Stripe-Webhook-Ereignis eintrifft, das sich keiner lokalen Zahlung zuordnen lässt, erscheint es hier — mit Zeitpunkt, Event-Typ, Grund und dem rohen Payload-Ausschnitt zur Nachverfolgung."
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="flex flex-col">
            {zeilen.map((z) => (
              <Zeile key={z.id} zeile={z} />
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
